import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { RuntimeFlag } from '../../generated/api/types.gen.js';
import { Button, Callout, Modal } from '../../components/ui/index.js';
import { Form, SubmitButton, TextFormField, useZodForm } from '../../components/form/index.js';
import { runtimeFlagConfirmSchema } from './runtime-flag-rules.js';

interface RuntimeFlagConfirmModalProps {
  flag: RuntimeFlag | null;
  onClose: () => void;
  onConfirm: (flag: RuntimeFlag) => void;
}

interface ConfirmFormProps {
  flag: RuntimeFlag;
  onClose: () => void;
  onConfirm: (flag: RuntimeFlag) => void;
}

const ConfirmForm: React.FC<ConfirmFormProps> = ({ flag, onClose, onConfirm }) => {
  const form = useZodForm(runtimeFlagConfirmSchema(flag), { defaultValues: { value: true, confirmation: '' } });

  return (
    <Form
      form={form}
      onSubmit={() => {
        onConfirm(flag);
        onClose();
      }}
    >
      <Callout tone="danger" icon={AlertTriangle} title="This enables real order placement">
        {flag.description}
      </Callout>
      <TextFormField
        name="confirmation"
        label={`Type ${flag.key} to confirm`}
        placeholder={flag.key}
        autoComplete="off"
        required
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <SubmitButton variant="danger">Turn on</SubmitButton>
      </div>
    </Form>
  );
};

/** Typed confirmation before a dangerous flag is switched on. */
export const RuntimeFlagConfirmModal: React.FC<RuntimeFlagConfirmModalProps> = ({ flag, onClose, onConfirm }) => (
  <Modal open={flag !== null} onClose={onClose} title={flag ? `Turn on ${flag.label}?` : 'Turn on flag'}>
    {flag && <ConfirmForm key={flag.key} flag={flag} onClose={onClose} onConfirm={onConfirm} />}
  </Modal>
);
